import { useCallback, useEffect, useRef, useState } from "react";
import { canvasToOptimizedBlob, AVATAR_MAX_SIDE } from "@/lib/optimizeImage";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Loader2, ZoomIn } from "lucide-react";

/**
 * Dialog de recadrage carré d'une image avant upload d'avatar.
 * Aucune dépendance externe : rendu CSS pour l'aperçu, export via <canvas>.
 *
 * Pourquoi un carré exporté en 512x512 : l'aperçu rond du profil utilise
 * object-cover ; un carré source garantit un cercle net et non déformé.
 */

/** Côté (px) de la zone de recadrage affichée dans le dialog. */
const VIEWPORT_SIZE = 288;
/** Côté (px) de l'image exportée. */
const OUTPUT_SIZE = AVATAR_MAX_SIDE;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

interface AvatarCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fichier image sélectionné par l'utilisateur. */
  file: File | null;
  /** Appelé avec le carré recadré (JPEG 512x512). */
  onCropped: (blob: Blob) => void | Promise<void>;
  saving?: boolean;
}

interface Point {
  x: number;
  y: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const AvatarCropDialog = ({
  open,
  onOpenChange,
  file,
  onCropped,
  saving = false,
}: AvatarCropDialogProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [exporting, setExporting] = useState(false);

  const dragStart = useRef<Point | null>(null);
  const offsetStart = useRef<Point>({ x: 0, y: 0 });

  // Chargement de l'image sélectionnée + nettoyage de l'object URL.
  useEffect(() => {
    if (!file || !open) {
      setImage(null);
      setImageUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });

    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;

    return () => URL.revokeObjectURL(url);
  }, [file, open]);

  /** Échelle « cover » : la plus petite dimension remplit le viewport. */
  const baseScale = image
    ? VIEWPORT_SIZE / Math.min(image.naturalWidth, image.naturalHeight)
    : 1;
  const displayedWidth = image ? image.naturalWidth * baseScale * zoom : 0;
  const displayedHeight = image ? image.naturalHeight * baseScale * zoom : 0;
  const maxOffsetX = Math.max(0, (displayedWidth - VIEWPORT_SIZE) / 2);
  const maxOffsetY = Math.max(0, (displayedHeight - VIEWPORT_SIZE) / 2);

  // Le déplacement reste borné : pas de bande vide dans le cadre.
  useEffect(() => {
    setOffset((prev) => ({
      x: clamp(prev.x, -maxOffsetX, maxOffsetX),
      y: clamp(prev.y, -maxOffsetY, maxOffsetY),
    }));
  }, [maxOffsetX, maxOffsetY]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!image) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, y: e.clientY };
    offsetStart.current = offset;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setOffset({
      x: clamp(offsetStart.current.x + dx, -maxOffsetX, maxOffsetX),
      y: clamp(offsetStart.current.y + dy, -maxOffsetY, maxOffsetY),
    });
  };

  const handlePointerUp = () => {
    dragStart.current = null;
  };

  const handleConfirm = useCallback(async () => {
    if (!image) return;
    setExporting(true);

    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas indisponible");

      // Ratio entre le viewport affiché et l'export final.
      const ratio = OUTPUT_SIZE / VIEWPORT_SIZE;
      const drawWidth = displayedWidth * ratio;
      const drawHeight = displayedHeight * ratio;
      const drawX = (OUTPUT_SIZE - drawWidth) / 2 + offset.x * ratio;
      const drawY = (OUTPUT_SIZE - drawHeight) / 2 + offset.y * ratio;

      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

      // WebP 0,85, JPEG en secours si le navigateur ne sait pas l'encoder.
      const blob = await canvasToOptimizedBlob(canvas);
      if (!blob) throw new Error("export impossible");

      await onCropped(blob);
    } finally {
      setExporting(false);
    }
  }, [image, displayedWidth, displayedHeight, offset, onCropped]);

  const busy = exporting || saving;

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      {/* z-[70] : au-dessus du voile d'onboarding (z-[60]) pendant l'inscription */}
      <DialogContent className="z-[70] max-w-md" overlayClassName="z-[70]">
        <DialogHeader>
          <DialogTitle>Recadrer la photo de profil</DialogTitle>
          <DialogDescription>
            Déplacez et zoomez l'image pour choisir la zone affichée dans votre
            cercle de profil.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <div
            className="relative overflow-hidden rounded-lg bg-muted touch-none select-none cursor-grab active:cursor-grabbing"
            style={{ width: VIEWPORT_SIZE, height: VIEWPORT_SIZE }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {imageUrl && image ? (
              <>
                <img
                  src={imageUrl}
                  alt="Aperçu du recadrage"
                  draggable={false}
                  className="absolute left-1/2 top-1/2 max-w-none pointer-events-none"
                  style={{
                    width: displayedWidth,
                    height: displayedHeight,
                    transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                  }}
                />
                {/* Repère circulaire : montre le rendu final de l'avatar. */}
                <div className="pointer-events-none absolute inset-0 rounded-full border-2 border-border/80" />
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          <div className="flex w-full items-center gap-3">
            <ZoomIn className="h-4 w-4 text-muted-foreground" />
            <Slider
              value={[zoom]}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              onValueChange={([value]) => setZoom(value)}
              disabled={!image || busy}
              aria-label="Zoom"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Annuler
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!image || busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarCropDialog;
